'use client'

import { useRouter } from 'next/navigation'
import type { Lang } from '@/lib/i18n'
import { syncLanguagePreference } from '@/app/lang-actions'

// Two-segment control (EN | ES) with the ACTIVE language highlighted.
// The old single-button version showed the *destination* language, which
// read as "backwards" (page in English showed "ES"). Showing both, with
// the current one highlighted, is unambiguous.
export default function LangToggle({ current }: { current: Lang }) {
  const router = useRouter()

  function setLang(next: Lang) {
    if (next === current) return
    document.cookie = `trc-lang=${next};path=/;max-age=31536000`
    // Mirror the choice onto the member's stored preference so the two never
    // drift (no-op for anonymous landing visitors). Audit CLI-04.
    syncLanguagePreference(next).catch(() => {})
    router.refresh()
  }

  function Seg({ lang, label }: { lang: Lang; label: string }) {
    const active = current === lang
    return (
      <button
        type="button"
        onClick={() => setLang(lang)}
        aria-pressed={active}
        aria-label={lang === 'en' ? 'English' : 'Español'}
        className={`px-2.5 py-1 rounded text-xs font-bold transition-colors ${
          active
            ? 'bg-trc-blue text-white'
            : 'text-trc-ash hover:text-trc-blue'
        }`}
      >
        {label}
      </button>
    )
  }

  return (
    <div
      role="group"
      aria-label="Language / Idioma"
      className="inline-flex items-center gap-0.5 border border-trc-ash-line rounded-md p-0.5 min-h-[40px]"
    >
      <Seg lang="en" label="EN" />
      <Seg lang="es" label="ES" />
    </div>
  )
}
